import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import facturaVentaService from '../../services/facturaVentaService';
import notaCreditoVentaService from '../../services/notaCreditoVentaService';
import notaDebitoVentaService from '../../services/notaDebitoVentaService';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatCurrency, formatDateLocal, formatClienteConNro } from '../../utils/formatters';

export type TipoDetalleComprobanteVenta = 'factura' | 'nota-debito' | 'nota-credito';

export interface DetalleComprobanteVentaDialogProps {
  open: boolean;
  onClose: () => void;
  nroFormulario: number | null;
  tipo: TipoDetalleComprobanteVenta | null;
}

interface LineaDetalle {
  key: string;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  cantidad?: number;
  um?: string;
  precioUnitario?: number;
  porcDesc?: number;
  subtotal?: number;
}

interface VistaComprobante {
  titulo: string;
  nroDoc: string;
  fechaEmision?: string | Date | null;
  fechaVencim?: string | Date | null;
  cliente: string;
  cuit?: string;
  usuario?: string;
  caeNro?: string;
  descripcion?: string;
  importeTotal: number;
  saldoPendiente?: number | null;
  lineas: LineaDetalle[];
}

const TITULO_POR_TIPO: Record<TipoDetalleComprobanteVenta, string> = {
  factura: 'Detalle de Factura',
  'nota-debito': 'Detalle de Nota de Débito',
  'nota-credito': 'Detalle de Nota de Crédito',
};

const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim().toUpperCase() || '-';
  const suc = String(nroSuc ?? 0).padStart(5, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  return formatDateLocal(fecha) || '-';
};

const DetalleComprobanteVentaDialog: React.FC<DetalleComprobanteVentaDialogProps> = ({
  open,
  onClose,
  nroFormulario,
  tipo,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [vista, setVista] = useState<VistaComprobante | null>(null);

  useEffect(() => {
    if (!open || !tipo || nroFormulario == null || nroFormulario <= 0) {
      setVista(null);
      return;
    }

    let cancelled = false;

    const cargar = async () => {
      setLoading(true);
      setVista(null);
      try {
        let data: VistaComprobante;

        if (tipo === 'factura') {
          const f = await facturaVentaService.getFacturaVenta(nroFormulario);
          const tieneComprobante =
            Boolean((f.letra ?? '').trim()) && f.nroSuc != null && f.nroComprobante != null;
          data = {
            titulo: TITULO_POR_TIPO.factura,
            nroDoc: tieneComprobante
              ? formatoLetraSucNro(f.letra, f.nroSuc, f.nroComprobante)
              : `#${f.nroFormulario}`,
            fechaEmision: f.fechaEmision,
            fechaVencim: f.fechaVencim,
            cliente: formatClienteConNro(f.nroCliente, f.nombre),
            cuit: f.cuit,
            usuario: f.usuario,
            caeNro: f.caeNro,
            descripcion: f.descripcion,
            importeTotal: Number(f.importeTotal ?? 0),
            saldoPendiente: f.saldoPendiente != null ? Number(f.saldoPendiente) : null,
            lineas: (f.detalles || []).map((d, idx) => ({
              key: `${d.nroItem ?? idx}-${d.codigoArticulo ?? ''}`,
              codigoArticulo: d.codigoArticulo,
              descripcionArticulo: d.descripcionArticulo,
              cantidad: d.cantidad,
              um: d.um,
              precioUnitario: d.precioUnitario,
              porcDesc: d.porcDesc,
              subtotal: d.subtotal,
            })),
          };
        } else if (tipo === 'nota-debito') {
          const n = await notaDebitoVentaService.getNotaDebitoVenta(nroFormulario);
          const tieneComprobante =
            Boolean((n.letra ?? '').trim()) && n.nroSuc != null && n.nroComprobante != null;
          data = {
            titulo: TITULO_POR_TIPO['nota-debito'],
            nroDoc: tieneComprobante
              ? formatoLetraSucNro(n.letra, n.nroSuc, n.nroComprobante)
              : `#${n.nroFormulario}`,
            fechaEmision: n.fechaEmision,
            cliente: formatClienteConNro(n.nroCliente, n.nombre),
            cuit: n.cuit,
            usuario: n.usuario,
            caeNro: n.caeNro,
            descripcion: n.descripcion,
            importeTotal: Number(n.importeTotal ?? 0),
            lineas: (n.detalles || []).map((d, idx) => ({
              key: `${d.nroItem ?? idx}-${d.codigoArticulo ?? ''}`,
              codigoArticulo: d.codigoArticulo,
              descripcionArticulo: d.descripcionArticulo,
              cantidad: d.cantidad,
              um: d.um,
              precioUnitario: d.precioUnitario,
              porcDesc: d.porcDesc,
              subtotal: d.subtotal,
            })),
          };
        } else {
          const n = await notaCreditoVentaService.getNotaCreditoVenta(nroFormulario);
          const tieneComprobante =
            Boolean((n.letra ?? '').trim()) && n.nroSuc != null && n.nroComprobante != null;
          data = {
            titulo: TITULO_POR_TIPO['nota-credito'],
            nroDoc: tieneComprobante
              ? formatoLetraSucNro(n.letra, n.nroSuc, n.nroComprobante)
              : `#${n.nroFormulario}`,
            fechaEmision: n.fechaEmision,
            cliente: formatClienteConNro(n.nroCliente, n.nombre),
            cuit: n.cuit,
            usuario: n.usuario,
            caeNro: n.caeNro,
            descripcion: n.descripcion,
            importeTotal: Number(n.importeTotal ?? 0),
            lineas: (n.detalles || []).map((d, idx) => ({
              key: `${d.nroItem ?? idx}-${d.codigoArticulo ?? ''}`,
              codigoArticulo: d.codigoArticulo,
              descripcionArticulo: d.descripcionArticulo,
              cantidad: d.cantidad,
              um: d.um,
              precioUnitario: d.precioUnitario,
              porcDesc: d.porcDesc,
              subtotal: d.subtotal,
            })),
          };
        }

        if (!cancelled) setVista(data);
      } catch (error: unknown) {
        if (!cancelled) {
          enqueueSnackbar(
            getApiErrorMessage(error, 'No se pudo cargar el detalle del comprobante'),
            { variant: 'error' }
          );
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void cargar();
    return () => {
      cancelled = true;
    };
  }, [open, tipo, nroFormulario, enqueueSnackbar, onClose]);

  const tituloDialog =
    vista != null
      ? `${vista.titulo} ${vista.nroDoc}`
      : tipo
        ? TITULO_POR_TIPO[tipo]
        : 'Detalle de comprobante';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{tituloDialog}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : vista == null ? (
          <Typography variant="body2" color="text.secondary">
            No hay datos de detalle para mostrar.
          </Typography>
        ) : (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Fecha emisión
                </Typography>
                <Typography variant="body2">{formatearFecha(vista.fechaEmision)}</Typography>
              </Grid>
              {vista.fechaVencim != null && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Vencimiento
                  </Typography>
                  <Typography variant="body2">{formatearFecha(vista.fechaVencim)}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Cliente
                </Typography>
                <Typography variant="body2">{vista.cliente || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  CUIT
                </Typography>
                <Typography variant="body2">{vista.cuit?.trim() || '-'}</Typography>
              </Grid>
              {vista.usuario ? (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Usuario
                  </Typography>
                  <Typography variant="body2">{vista.usuario}</Typography>
                </Grid>
              ) : null}
              {vista.caeNro ? (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    CAE
                  </Typography>
                  <Typography variant="body2">{vista.caeNro}</Typography>
                </Grid>
              ) : null}
              {vista.descripcion ? (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Descripción
                  </Typography>
                  <Typography variant="body2">{vista.descripcion}</Typography>
                </Grid>
              ) : null}
            </Grid>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
              Ítems
            </Typography>
            <TableContainer sx={{ mb: 2, maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Cantidad
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>UM</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Precio unit.
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      % Desc
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Subtotal
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vista.lineas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>Sin ítems</TableCell>
                    </TableRow>
                  ) : (
                    vista.lineas.map((d) => (
                      <TableRow key={d.key}>
                        <TableCell>{d.codigoArticulo || '-'}</TableCell>
                        <TableCell>{d.descripcionArticulo || '-'}</TableCell>
                        <TableCell align="right">
                          {d.cantidad != null
                            ? Number(d.cantidad).toLocaleString('es-AR', { minimumFractionDigits: 2 })
                            : '-'}
                        </TableCell>
                        <TableCell>{d.um || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(d.precioUnitario ?? 0)}</TableCell>
                        <TableCell align="right">
                          {d.porcDesc != null ? `${Number(d.porcDesc).toFixed(1)}%` : '-'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(d.subtotal ?? 0)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, flexWrap: 'wrap' }}>
              {vista.saldoPendiente != null && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">
                    Saldo pendiente
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} color="error.main">
                    {formatCurrency(vista.saldoPendiente)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary">
                  Importe total
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {formatCurrency(vista.importeTotal)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetalleComprobanteVentaDialog;
